import type { SectionListData } from 'react-native';

/**
 * Helper to construct the `getItemLayout` function for React Native's SectionLists.
 */
export function getSectionListItemLayout<ItemT = any>({
    getItemHeight,
    getItemSeparatorHeight = () => 0,
    getSectionSeparatorHeight = () => 0,
    getSectionHeaderHeight = () => 0,
    getSectionFooterHeight = () => 0,
    listHeaderHeight = 0,
    stickySectionHeadersEnabled,
}: {
    getItemHeight: (info: {
        item: ItemT;
        section: SectionListData<ItemT>;
        index: number;
    }) => number;
    getItemSeparatorHeight?: (info: {
        item: ItemT;
        section: SectionListData<ItemT>;
        index: number;
        leadingItem: ItemT | undefined;
        trailingItem: ItemT | undefined;
        leadingSection: SectionListData<ItemT> | undefined;
        trailingSection: SectionListData<ItemT> | undefined;
    }) => number;
    getSectionSeparatorHeight?: (info: {
        section: SectionListData<ItemT>;
        index: number;
        leadingItem: ItemT | undefined;
        trailingItem: ItemT | undefined;
        leadingSection: SectionListData<ItemT> | undefined;
        trailingSection: SectionListData<ItemT> | undefined;
    }) => number;
    getSectionHeaderHeight?: (info: { section: SectionListData<ItemT>; index: number }) => number;
    getSectionFooterHeight?: (info: { section: SectionListData<ItemT>; index: number }) => number;
    listHeaderHeight?: number | (() => number);
    stickySectionHeadersEnabled?: boolean;
}) {
    return (sections: SectionListData<ItemT>[], index: number) => {
        let length = 0;
        let offset = typeof listHeaderHeight === 'function' ? listHeaderHeight() : listHeaderHeight;
        let currentIndex = 0;
        const sectionsLength = sections.length;

        for (
            let sectionIndex = 0;
            sectionIndex < sectionsLength && (currentIndex < index || sectionIndex === 0);
            sectionIndex++
        ) {
            const section = sections[sectionIndex];
            const sectionData = sections[sectionIndex].data;
            const dataLength = sectionData.length;

            const leadingSection = sections[sectionIndex - 1];
            const trailingSection = sections[sectionIndex + 1];

            const info = { section, index: sectionIndex };
            const sectionTopSeparatorHeight = getSectionSeparatorHeight({
                section,
                index: sectionIndex,
                leadingItem: undefined,
                trailingItem: sectionData[0],
                leadingSection,
                trailingSection,
            });
            offset +=
                length +
                sectionTopSeparatorHeight +
                (stickySectionHeadersEnabled && sectionIndex === 1
                    ? getSectionHeaderHeight({ section: sections[0], index: 0 })
                    : 0);
            length =
                stickySectionHeadersEnabled && sectionIndex === 0
                    ? 0
                    : getSectionHeaderHeight(info);

            if (sectionIndex !== 0) {
                // the first section header doesn't count
                currentIndex++;
            }

            for (
                let dataIndex = 0;
                dataIndex < dataLength &&
                (currentIndex < index || (sectionIndex === 0 && dataIndex === 0));
                dataIndex++
            ) {
                offset += length;

                const item = sectionData[dataIndex];
                const itemSeparatorHeight =
                    dataIndex > 0 && dataIndex < dataLength - 1
                        ? getItemSeparatorHeight({
                              item,
                              section,
                              index: dataIndex,
                              leadingItem: sectionData[dataIndex - 1],
                              trailingItem: sectionData[dataIndex + 1],
                              leadingSection,
                              trailingSection,
                          })
                        : 0;
                length = getItemHeight({ item, section, index: dataIndex }) + itemSeparatorHeight;
                currentIndex++;
            }

            if (currentIndex < index) {
                offset += getSectionSeparatorHeight({
                    section,
                    index: sectionIndex,
                    leadingItem: sectionData[sectionData.length - 1],
                    trailingItem: undefined,
                    leadingSection,
                    trailingSection,
                });
            }

            if (currentIndex < index) {
                const sectionBottomSeparatorHeight = getSectionSeparatorHeight({
                    section,
                    index: sectionIndex,
                    leadingItem: sectionData[sectionData.length - 1],
                    trailingItem: undefined,
                    leadingSection,
                    trailingSection,
                });
                offset += length + sectionBottomSeparatorHeight;
                length = getSectionFooterHeight(info);
                currentIndex++;
            }
        }

        return {
            index,
            length,
            offset,
        };
    };
}
